import { motion } from "motion/react";
import { useNavigate } from "react-router";
import { useSelector } from "react-redux";
import { RootState } from "../../store";
import { ArrowRight, Video, MessageCircle } from "lucide-react";
import { ImageWithFallback } from "./figma/ImageWithFallback";

export default function Hero() {
  const navigate = useNavigate();
  const { user } = useSelector((state: RootState) => state.auth);

  return (
    <section className="pt-24 pb-12 px-4 sm:pt-32 sm:pb-20 sm:px-6 bg-gradient-to-b from-purple-50 via-pink-50 to-white">
      <div className="max-w-7xl mx-auto">
        <div className="grid lg:grid-cols-2 gap-8 sm:gap-12 items-center">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
          >
            <h1 className="text-4xl sm:text-5xl md:text-6xl font-bold text-gray-900 mb-4 sm:mb-6 leading-tight">
              Your Voice Matters. Share Your{" "}
              <span className="bg-gradient-to-r from-purple-600 to-pink-600 bg-clip-text text-transparent">
                Journey
              </span>{" "}
              Anytime
            </h1>
            <p className="text-lg sm:text-xl text-gray-600 mb-4 sm:mb-6 leading-relaxed">
              Step into a safe, anonymous sanctuary where every emotion is heard
              and validated. Connect instantly with compassionate listeners who
              care about your well-being—because you never have to walk alone.
            </p>

            <div className="mb-6 sm:mb-8 rounded-3xl border border-purple-200 bg-white/80 p-4 sm:p-5 shadow-sm backdrop-blur-sm">
              <p className="text-lg font-semibold italic text-purple-700 leading-relaxed">
                “You don’t always need advice. Sometimes, you just need someone
                to listen.”
              </p>
              <p className="mt-2 text-sm text-gray-600">
                That is the heart of FeelTalk — a gentle, compassionate space
                where your feelings are met with care.
              </p>
            </div>

            <div className="flex flex-col sm:flex-row gap-3 sm:gap-4 mb-5 sm:mb-6">
              {!user && (
                <>
                  <button
                    onClick={() => navigate("/select-emotion")}
                    className="group px-8 py-4 bg-gradient-to-r from-purple-500 to-pink-500 text-white rounded-full font-medium hover:shadow-xl transition-all flex items-center justify-center gap-2"
                  >
                    Start Talking
                    <ArrowRight className="w-5 h-5 group-hover:translate-x-1 transition-transform" />
                  </button>

                  <button
                    onClick={() => navigate("/become-listener")}
                    className="group px-8 py-4 bg-white border-2 border-purple-200 text-purple-600 rounded-full font-medium hover:bg-purple-50 hover:border-purple-300 transition-all flex items-center justify-center gap-2"
                  >
                    <span>Become a Listener</span>
                    <ArrowRight className="w-5 h-5 group-hover:translate-x-1 transition-transform" />
                  </button>
                </>
              )}

              {user?.role === "talking" && (
                <button
                  onClick={() => navigate("/select-emotion")}
                  className="group px-8 py-4 bg-gradient-to-r from-purple-500 to-pink-500 text-white rounded-full font-medium hover:shadow-xl transition-all flex items-center justify-center gap-2"
                >
                  Start Talking
                  <ArrowRight className="w-5 h-5 group-hover:translate-x-1 transition-transform" />
                </button>
              )}

              {user?.role === "listener" && (
                <button
                  onClick={() => navigate("/listener/dashboard")}
                  className="group px-8 py-4 bg-gradient-to-r from-purple-500 to-pink-500 text-white rounded-full font-medium hover:shadow-xl transition-all flex items-center justify-center gap-2"
                >
                  <span>Listener Dashboard</span>
                  <ArrowRight className="w-5 h-5 group-hover:translate-x-1 transition-transform" />
                </button>
              )}
            </div>

            <div className="flex items-center gap-2 sm:gap-3 text-sm font-medium text-gray-600 flex-wrap">
              <span className="flex items-center gap-1">
                🔒 Anonymous &amp; Private
              </span>
              <span className="text-gray-300">·</span>
              <span className="flex items-center gap-1">
                💬 Text &amp; Voice
              </span>
              <span className="text-gray-300">·</span>
              <span className="flex items-center gap-1">Available 24/7</span>
            </div>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, scale: 0.95 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ duration: 0.6, delay: 0.2 }}
            className="relative mt-8 lg:mt-0"
          >
            <div className="relative rounded-3xl overflow-hidden shadow-2xl">
              <ImageWithFallback
                src="https://images.unsplash.com/photo-1758876201548-ade1eff8b169?crop=entropy&cs=tinysrgb&fit=max&fm=webp&q=80&w=600"
                alt="Person in peaceful conversation"
                className="w-full h-full object-cover"
                fetchPriority="high"
                loading="eager"
                decoding="async"
                width={600}
                height={400}
              />
              <div className="absolute inset-0 bg-gradient-to-t from-purple-900/20 to-transparent"></div>
            </div>

            {/* Floating elements */}
            <motion.div
              animate={{ y: [0, -10, 0] }}
              transition={{ duration: 3, repeat: Infinity }}
              className="absolute -top-4 -right-4 w-24 h-24 bg-gradient-to-br from-purple-300 to-pink-300 rounded-full blur-2xl opacity-60 hidden sm:block"
            ></motion.div>
            <motion.div
              animate={{ y: [0, 10, 0] }}
              transition={{ duration: 3, repeat: Infinity, delay: 1 }}
              className="absolute -bottom-4 -left-4 w-32 h-32 bg-gradient-to-br from-blue-300 to-purple-300 rounded-full blur-2xl opacity-60 hidden sm:block"
            ></motion.div>
          </motion.div>
        </div>
      </div>
    </section>
  );
}
